import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import { adminApi, AdminProduct } from '../services/adminApi';
import { API_BASE_URL } from '../config/environment';
import { useToast } from '../contexts/ToastContext';
import './AdminContent.css';
import { proxyBucketUrl, compressImageFile, handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageUtils';

interface Banner {
  id: number;
  title: string;
  subtitle?: string;
  imageUrl: string;
  linkUrl?: string;
  buttonText?: string;
  isActive: boolean;
  order: number;
}


interface SiteSettings {
  id: number;
  heroTitle: string;
  heroSubtitle: string;
  aboutText: string;
  contactEmail: string;
  contactPhone: string;
  socialLinks: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
}

const AdminContent: React.FC = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [siteSettings, setSiteSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'shoplook'>('shoplook');
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [showBannerModal, setShowBannerModal] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const [bannersData, settingsData] = await Promise.all([
        adminApi.getBanners().catch(() => []),
        adminApi.getSiteSettings().catch(() => null)
      ]);
      
      // Handle banners data
      if (Array.isArray(bannersData)) {
        setBanners(bannersData);
      } else if (bannersData && Array.isArray(bannersData.data)) {
        setBanners(bannersData.data);
      } else {
        setBanners([]);
      }
      
      
      // Handle settings data
      setSiteSettings(settingsData);
    } catch (err: any) {
      console.error('Failed to load content:', err);
      setError(err.message || 'Failed to load content');
      setBanners([]);
      setSiteSettings(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBanner = async (banner: Banner) => {
    try {
      if (banner.id) {
        await adminApi.updateBanner(banner.id, banner);
      } else {
        await adminApi.createBanner(banner);
      }
      await loadContent();
      setShowBannerModal(false);
      setEditingBanner(null);
    } catch (err: any) {
      setError(err.message || 'Failed to save banner');
    }
  };

  const handleDeleteBanner = async (bannerId: number) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await adminApi.deleteBanner(bannerId);
        await loadContent();
      } catch (err: any) {
        setError(err.message || 'Failed to delete banner');
      }
    }
  };

  const toggleBannerStatus = async (bannerId: number, isActive: boolean) => {
    try {
      await adminApi.updateBannerStatus(bannerId, isActive);
      await loadContent();
    } catch (err: any) {
      setError(err.message || 'Failed to update banner status');
    }
  };


  const handleSaveSettings = async (settings: SiteSettings) => {
    try {
      await adminApi.updateSiteSettings(settings);
      await loadContent();
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading Content...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-content">
        <div className="content-header">
          <div className="header-content">
            <h1>Content Management</h1>
            <p>Manage homepage content and site settings</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={loadContent}>
              <span className="btn-icon">🔄</span>
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
            <button onClick={() => setError('')} className="close-error">×</button>
          </div>
        )}


        <div className="tab-content">
          {activeTab === 'shoplook' && (
            <ShopTheLookTab />
          )}
        </div>

      </div>
    </AdminLayout>
  );
};

// New interfaces for the homepage sections

interface TrendSection {
  id: number;
  title: string;
  description: string;
  image: string;
  linkUrl: string;
  isActive: boolean;
  order: number;
}

interface Hotspot {
  id: number;
  x: number; // percentage from left
  y: number; // percentage from top
  productId: number;
  customTitle?: string; // Custom title for sold items
  product?: AdminProduct;
}

interface ShopLookSection {
  id: number;
  title: string;
  description: string;
  image: string;
  images?: string[];
  hotspots: Hotspot[];
  isActive: boolean;
  status?: string; // Add status field for draft/published
  order: number;
}

interface OutfitHotspot {
  id: number;
  x: number; // percentage from left
  y: number; // percentage from top
  productId: number;
  customTitle?: string; // Custom title for sold items
  product?: AdminProduct;
}

interface OutfitSection {
  id: number;
  title: string;
  description: string;
  image: string;
  hotspots: OutfitHotspot[];
  isActive: boolean;
  order: number;
}


// Hot Trends Tab Component - Now showing actual products from database
const HotTrendsTab: React.FC = () => {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [hotTrendProducts, setHotTrendProducts] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      // Fetch all products from the database
      const response = await adminApi.getProducts({ limit: 1000 }); // Get all products
      const productsData = Array.isArray(response) ? response : response.products || [];
      
      // Filter only in-stock products
      const inStockProducts = productsData.filter((product: AdminProduct) => product.inStock);
      setProducts(inStockProducts);
      
      // Load saved hot trend selections from localStorage (in real app, this would come from backend)
      const saved = localStorage.getItem('hotTrendProducts');
      if (saved) {
        setHotTrendProducts(new Set(JSON.parse(saved)));
      }
    } catch (err: any) {
      console.error('Failed to load products:', err);
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const toggleProductHotTrend = (productId: number) => {
    const newHotTrends = new Set(hotTrendProducts);
    
    if (newHotTrends.has(productId)) {
      newHotTrends.delete(productId);
    } else {
      newHotTrends.add(productId);
    }
    
    setHotTrendProducts(newHotTrends);
    // Save to localStorage (in real app, this would save to backend)
    localStorage.setItem('hotTrendProducts', JSON.stringify(Array.from(newHotTrends)));
  };

  const filteredProducts = products.filter(product =>
    searchTerm === '' || 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (typeof product.category === 'object' && product.category?.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const hotTrendProductsList = products.filter(product => hotTrendProducts.has(product.id));

  if (loading) {
    return (
      <div className="homepage-section">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading Products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage-section">
      <div className="section-header">
        <div className="header-info">
          <h2>🔥 Hot Trends</h2>
          <p>Select products to feature as hot trends on the homepage ({hotTrendProducts.size} selected)</p>
        </div>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            style={{ 
              padding: '8px 12px', 
              borderRadius: '8px', 
              border: '1px solid #d1d5db',
              marginRight: '12px',
              minWidth: '200px'
            }}
          />
          <button className="btn btn-secondary" onClick={loadProducts}>
            <span className="btn-icon">🔄</span>
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button onClick={() => setError('')} className="close-error">×</button>
        </div>
      )}

      {/* Hot Trends Summary */}
      {hotTrendProducts.size > 0 && (
        <div style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 12px 0' }}>🌟 Current Hot Trends ({hotTrendProductsList.length} products live)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {hotTrendProductsList.map(product => (
              <div key={product.id} style={{ 
                background: 'rgba(255,255,255,0.1)', 
                padding: '12px', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <img 
                  src={proxyBucketUrl(product.images[0]) || PLACEHOLDER_IMAGE} 
                  alt={product.name}
                  style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' }}
                />
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>{product.name}</div>
                  <div style={{ fontSize: '12px', opacity: '0.8' }}>€{product.price}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="content-grid">
        {filteredProducts.map((product) => {
          const isHotTrend = hotTrendProducts.has(product.id);
          const categoryName = typeof product.category === 'object' ? product.category?.name : product.category;
          
          return (
            <div key={product.id} className={`content-card ${isHotTrend ? 'hot-trend-active' : ''}`}>
              <div className="card-image">
                <img 
                  src={proxyBucketUrl(product.images[0]) || PLACEHOLDER_IMAGE} 
                  alt={product.name}
                  onError={handleImageError}
                />
                {isHotTrend && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: 'linear-gradient(135deg, #ff006e, #8338ec)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    🔥 HOT TREND
                  </div>
                )}
              </div>
              
              <div className="card-content">
                <div className="content-info">
                  <h3>{product.name}</h3>
                  <p style={{ margin: '4px 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                    {categoryName} • €{product.price}
                  </p>
                  {product.description && (
                    <p style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.4' }}>
                      {product.description.length > 100 
                        ? product.description.substring(0, 100) + '...'
                        : product.description
                      }
                    </p>
                  )}
                </div>
                
                <div className="card-actions">
                  <div className="status-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={isHotTrend}
                        onChange={() => toggleProductHotTrend(product.id)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className={`status-text ${isHotTrend ? 'active' : 'inactive'}`}>
                      {isHotTrend ? 'Hot Trend' : 'Regular'}
                    </span>
                  </div>
                  
                  <div className="product-meta" style={{ 
                    fontSize: '12px', 
                    color: '#6b7280',
                    textAlign: 'right'
                  }}>
                    <div>Stock: {product.stockQuantity || 'N/A'}</div>
                    <div>ID: #{product.id}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredProducts.length === 0 && !loading && (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h3>No Products Found</h3>
          <p>No in-stock products match your search criteria</p>
        </div>
      )}
    </div>
  );
};

// Shop the Look Tab Component - Advanced hotspot editor
const ShopTheLookTab: React.FC = () => {
  const { showToast } = useToast();
  const [looks, setLooks] = useState<ShopLookSection[]>([]);
  const [editingLook, setEditingLook] = useState<ShopLookSection | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Load looks from API with admin status
  useEffect(() => {
    const loadLooks = async () => {
      try {
        console.log('👗 Loading admin outfits with all statuses...');
        const response = await adminApi.getOutfits({ includeStatus: 'all' });
        console.log('✅ Admin outfits loaded:', response);
        
        if (Array.isArray(response) && response.length > 0) {
          // Convert backend format to frontend format
          const convertedLooks = response.map((item: any) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            image: item.image,
            images: item.images || [item.image],
            hotspots: item.hotspots || [],
            isActive: item.isActive,
            status: item.status || 'published', // Add status field
            order: 1
          }));
          setLooks(convertedLooks);
        } else {
          setLooks([]);
        }
      } catch (error) {
        console.error('Failed to load Shop the Look data:', error);
        setLooks([]);
      }
    };
    
    loadLooks();
  }, []);

  const handleEditLook = (look: ShopLookSection) => {
    setEditingLook(look);
    setShowModal(true);
  };

  const handleAddLook = () => {
    setEditingLook({
      id: 0,
      title: "",
      description: "",
      image: "",
      images: [],
      hotspots: [],
      isActive: true,
      status: 'published',
      order: looks.length + 1
    });
    setShowModal(true);
  };

  const handleSaveLook = async (look: ShopLookSection) => {
    try {
      console.log('👗 Shop the Look: Saving with ID:', look.id, 'Title:', look.title);
      
      const outfitData = {
        title: look.title,
        description: look.description,
        image: look.image,
        images: look.images || [look.image],
        hotspots: look.hotspots,
        status: look.status || 'draft'
      };
      
      let result;
      if (look.id === 0 || !look.id) {
        // Create new
        console.log('➕ Creating new Shop the Look...');
        result = await adminApi.createOutfit(outfitData);
      } else {
        // Update existing
        console.log('✏️ Updating existing Shop the Look:', look.id);
        result = await adminApi.updateOutfit(look.id, outfitData);
      }
      console.log('✅ Shop the Look saved to database:', result);
      
      // Reload the looks to get updated data
      const response = await adminApi.getOutfits({ includeStatus: 'all' });
      
      if (Array.isArray(response) && response.length > 0) {
        const convertedLooks = response.map((item: any) => ({
          id: item.id,
          title: item.title,
          description: item.description,
          image: item.image,
          images: item.images || [item.image],
          hotspots: item.hotspots || [],
          isActive: item.isActive,
          status: item.status || 'published',
          order: 1
        }));
        setLooks(convertedLooks);
      }
    } catch (error) {
      console.error('Error saving Shop the Look:', error);
      showToast('Error saving Shop the Look', 'error');
    }
    
    setShowModal(false);
    setEditingLook(null);
  };

  const handleDeleteLook = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this look?')) {
      try {
        console.log(`🗑️ Deleting outfit ${id}...`);
        await adminApi.deleteOutfit(id);
        
        // Reload the looks to get updated data
        const response = await adminApi.getOutfits({ includeStatus: 'all' });
        
        if (Array.isArray(response) && response.length > 0) {
          const convertedLooks = response.map((item: any) => ({
            id: item.id,
            title: item.title,
            description: item.description,
            image: item.image,
            images: item.images || [item.image],
            hotspots: item.hotspots || [],
            isActive: item.isActive,
            status: item.status || 'published',
            order: 1
          }));
          setLooks(convertedLooks);
        } else {
          setLooks([]);
        }
        
        console.log('✅ Outfit deleted successfully');
      } catch (error) {
        console.error('Error deleting look:', error);
        showToast('Error deleting look', 'error');
      }
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      console.log(`👗 Admin: Changing outfit ${id} status to ${newStatus}`);
      
      await adminApi.updateOutfitStatus(id, newStatus);
      
      // Reload the looks to get updated data
      const response = await adminApi.getOutfits({ includeStatus: 'all' });
      
      if (Array.isArray(response) && response.length > 0) {
        const convertedLooks = response.map((item: any) => ({
          id: item.id,
          title: item.title,
          description: item.description,
          image: item.image,
          images: item.images || [item.image],
          hotspots: item.hotspots || [],
          isActive: item.isActive,
          status: item.status || 'published',
          order: 1
        }));
        setLooks(convertedLooks);
      }
      
      console.log(`✅ Outfit status updated successfully`);
    } catch (error) {
      console.error('Failed to update outfit status:', error);
      showToast('Failed to update outfit status. Please try again.', 'error');
    }
  };

  return (
    <div className="homepage-section">
      <div className="section-header">
        <div className="header-info">
          <h2>👗 Shop the Look</h2>
          <p>Create interactive outfit experiences with clickable hotspots</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleAddLook}>
            <span className="btn-icon">➕</span>
            Add New Look
          </button>
        </div>
      </div>

      {looks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <button className="btn btn-primary" onClick={handleAddLook}>
            <span className="btn-icon">➕</span>
            Create New Look
          </button>
        </div>
      )}

      <div className="content-grid">
        {looks.map((look) => (
          <div key={look.id} className={`content-card ${!look.isActive ? 'inactive' : ''}`}>
            <div className="card-image" style={{ position: 'relative' }}>
              <img 
                src={proxyBucketUrl(look.image)} 
                alt={look.title}
                onError={handleImageError}
                style={{ width: '100%', height: '200px', objectFit: 'cover' }}
              />
              
              {/* Show hotspots on preview */}
              {look.hotspots.map((hotspot) => (
                <div
                  key={hotspot.id}
                  style={{
                    position: 'absolute',
                    left: `${hotspot.x}%`,
                    top: `${hotspot.y}%`,
                    width: '20px',
                    height: '20px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: '2px solid #ff006e',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#ff006e',
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    animation: 'pulse 2s infinite'
                  }}
                >
                  {hotspot.id}
                </div>
              ))}
              
              <div className="image-overlay">
                <button 
                  className="change-image-btn"
                  onClick={() => handleEditLook(look)}
                >
                  ✏️ Edit Look
                </button>
              </div>
            </div>
            
            <div className="card-content">
              <div className="content-info">
                <h3>{look.title}</h3>
                <p>{look.description}</p>
                <div className="hotspots-info">
                  <strong>Hotspots: {look.hotspots.length}</strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Interactive points linking to products
                  </div>
                </div>
              </div>
              
              <div className="card-actions">
                <div className="status-controls">
                  <select
                    className={`status-select ${look.status || 'published'}`}
                    value={look.status || 'published'}
                    onChange={(e) => handleStatusChange(look.id, e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
                
                <div className="action-buttons">
                  <button 
                    className="btn-action btn-edit"
                    onClick={() => handleEditLook(look)}
                    title="Edit Look & Hotspots"
                  >
                    ✏️
                  </button>
                  <button 
                    className="btn-action btn-delete"
                    onClick={() => handleDeleteLook(look.id)}
                    title="Delete Look"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {looks.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">👗</div>
          <h3>No Looks Created</h3>
          <p>Create your first interactive shop the look experience</p>
          <button className="btn btn-primary" onClick={handleAddLook}>
            Create First Look
          </button>
        </div>
      )}

      {showModal && editingLook && (
        <ShopLookEditor
          look={editingLook}
          onSave={handleSaveLook}
          onCancel={() => {
            setShowModal(false);
            setEditingLook(null);
          }}
        />
      )}
    </div>
  );
};

// Outfits Tab Component - Manage 4 outfits for Outfits page
const OutfitsTab: React.FC = () => {
  const { showToast } = useToast();
  const [outfitsData, setOutfitsData] = useState<OutfitSection[]>([]);
  const [editingOutfit, setEditingOutfit] = useState<OutfitSection | null>(null);
  const [showOutfitModal, setShowOutfitModal] = useState(false);

  // Load outfits from database API on component mount
  useEffect(() => {
    loadOutfitsFromAPI();
  }, []);

  const loadOutfitsFromAPI = async () => {
    try {
      console.log('🎨 Admin: Loading outfits from API...');
      
      let response;
      try {
        response = await fetch(`${API_BASE_URL}/outfits/admin`);
      } catch (apiError) {
        console.log('❌ Primary API failed, trying localhost...');
        response = await fetch(`${API_BASE_URL}/outfits/admin`);
      }
      
      if (response.ok) {
        const outfitsArray = await response.json();
        console.log('✅ Admin outfits loaded:', outfitsArray?.length, 'outfits found');
        setOutfitsData(outfitsArray);
      } else {
        console.log('❌ API response not ok, using default data');
        // If API fails, set default data
        setOutfitsData(getDefaultOutfits());
      }
    } catch (error) {
      console.error('Failed to load outfits from API:', error);
      console.log('Using default outfits data as fallback');
      // Set default data if API fails
      setOutfitsData(getDefaultOutfits());
    }
  };

  const getDefaultOutfits = (): OutfitSection[] => [
    {
      id: 1,
      title: "Parisian Chic",
      description: "Classic French elegance meets modern attitude",
      image: "/outfits/outfit.jpeg",
      hotspots: [
        { id: 1, x: 42, y: 10, productId: 0 },
        { id: 2, x: 53, y: 45, productId: 0 },
        { id: 3, x: 30, y: 55, productId: 0 },
        { id: 4, x: 40, y: 75, productId: 0 }
      ],
      isActive: true,
      order: 1
    },
    {
      id: 2,
      title: "90s Grunge",
      description: "Raw energy with a vintage twist",
      image: "/outfits/outfit2.jpeg",
      hotspots: [
        { id: 1, x: 40, y: 1, productId: 0 },
        { id: 2, x: 50, y: 30, productId: 0 },
        { id: 3, x: 35, y: 60, productId: 0 },
        { id: 4, x: 50, y: 75, productId: 0 }
      ],
      isActive: true,
      order: 2
    },
    {
      id: 3,
      title: "Bohemian Dreams",
      description: "Free-spirited style for the modern nomad",
      image: "/outfits/outfit3.png",
      hotspots: [
        { id: 1, x: 50, y: 5, productId: 0 },
        { id: 2, x: 55, y: 30, productId: 0 },
        { id: 3, x: 50, y: 60, productId: 0 }
      ],
      isActive: true,
      order: 3
    },
    {
      id: 4,
      title: "Retro Colorblock",
      description: "Bold patterns and vintage vibes collide",
      image: "/outfits/outfit4.jpeg",
      hotspots: [
        { id: 1, x: 53, y: 9, productId: 0 },
        { id: 2, x: 45, y: 35, productId: 0 },
        { id: 3, x: 25, y: 70, productId: 0 },
        { id: 4, x: 55, y: 85, productId: 0 }
      ],
      isActive: true,
      order: 4
    }
  ];

  const handleEditOutfit = (outfit: OutfitSection) => {
    setEditingOutfit(outfit);
    setShowOutfitModal(true);
  };

  const handleAddOutfit = () => {
    setEditingOutfit({
      id: 0,
      title: "",
      description: "",
      image: "",
      hotspots: [],
      isActive: true,
      order: outfitsData.length + 1
    });
    setShowOutfitModal(true);
  };

  const handleSaveOutfit = async (outfit: OutfitSection) => {
    try {
      // Use API_BASE_URL for all environments
      const apiBaseUrl = API_BASE_URL;
      
      console.log('💾 Saving outfit:', outfit.id, outfit.title);
        
      const outfitData = {
        title: outfit.title,
        description: outfit.description,
        imageUrl: outfit.image,
        hotspots: outfit.hotspots,
        isActive: outfit.isActive,
        displayOrder: outfit.order,
        status: 'published'
      };

      if (outfit.id === 0 || !outfit.id) {
        // Create new outfit
        console.log('🆕 Creating new outfit...');
        const response = await fetch(`${apiBaseUrl}/outfits`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(outfitData)
        });

        if (response.ok) {
          await loadOutfitsFromAPI(); // Reload from database
          showToast('Outfit created successfully!', 'success');
        } else {
          throw new Error('Failed to create outfit');
        }
      } else {
        // Update existing outfit
        console.log('✏️ Updating existing outfit:', outfit.id);
        const response = await fetch(`${apiBaseUrl}/outfits/${outfit.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(outfitData)
        });

        console.log('🔄 Update response status:', response.status);
        const responseText = await response.text();
        console.log('📄 Update response:', responseText);

        if (response.ok) {
          await loadOutfitsFromAPI(); // Reload from database
          showToast('Outfit updated successfully!', 'success');
        } else {
          throw new Error(`Failed to update outfit: ${response.status} ${responseText}`);
        }
      }
      
      setShowOutfitModal(false);
      setEditingOutfit(null);
    } catch (error) {
      console.error('Error saving outfit:', error);
      showToast('Failed to save outfit. Please try again.', 'error');
    }
  };

  const handleDeleteOutfit = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this outfit?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/outfits/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          await loadOutfitsFromAPI(); // Reload from database
          showToast('Outfit deleted successfully!', 'success');
        } else {
          throw new Error('Failed to delete outfit');
        }
      } catch (error) {
        console.error('Error deleting outfit:', error);
        showToast('Failed to delete outfit. Please try again.', 'error');
      }
    }
  };

  const toggleOutfitStatus = async (id: number) => {
    const outfit = outfitsData.find(o => o.id === id);
    if (!outfit) return;

    try {
      const outfitData = {
        title: outfit.title,
        description: outfit.description,
        imageUrl: outfit.image,
        hotspots: outfit.hotspots,
        isActive: !outfit.isActive,
        displayOrder: outfit.order,
        status: 'published'
      };

      const response = await fetch(`${API_BASE_URL}/outfits/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(outfitData)
      });

      if (response.ok) {
        await loadOutfitsFromAPI(); // Reload from database
      } else {
        throw new Error('Failed to update outfit status');
      }
    } catch (error) {
      console.error('Error updating outfit status:', error);
      showToast('Failed to update outfit status. Please try again.', 'error');
    }
  };

  return (
    <div className="homepage-section">
      <div className="section-header">
        <div className="header-info">
          <h2>👘 Outfits Collection</h2>
          <p>Manage the 4 outfits displayed on the Outfits page with interactive hotspots</p>
        </div>
        <button className="btn btn-primary" onClick={handleAddOutfit}>
          <span className="btn-icon">➕</span>
          Add New Outfit
        </button>
      </div>

      <div className="content-grid">
        {outfitsData.map((outfit) => (
          <div key={outfit.id} className={`content-card ${!outfit.isActive ? 'inactive' : ''}`}>
            <div style={{ position: 'relative', height: 'auto', overflow: 'visible', background: '#f8f9fc', borderRadius: '8px', padding: '10px' }}>
              <img 
                src={proxyBucketUrl(outfit.image)} 
                alt={outfit.title}
                onError={handleImageError}
                style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block', borderRadius: '4px' }}
              />
              
              {/* Show hotspots on preview */}
              {outfit.hotspots.map((hotspot) => (
                <div
                  key={hotspot.id}
                  style={{
                    position: 'absolute',
                    left: `${hotspot.x}%`,
                    top: `${hotspot.y}%`,
                    width: '20px',
                    height: '20px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: '2px solid #ff006e',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#ff006e',
                    transform: 'translate(-50%, -50%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    animation: 'pulse 2s infinite'
                  }}
                >
                  {hotspot.id}
                </div>
              ))}
              
              <div className="image-overlay">
                <button 
                  className="change-image-btn"
                  onClick={() => handleEditOutfit(outfit)}
                >
                  ✏️ Edit Outfit
                </button>
              </div>
            </div>
            
            <div className="card-content">
              <div className="content-info">
                <h3>{outfit.title}</h3>
                <p>{outfit.description}</p>
                <div className="hotspots-info">
                  <strong>Hotspots: {outfit.hotspots.length}</strong>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Interactive points for outfit pieces
                  </div>
                </div>
              </div>
              
              <div className="card-actions">
                <div className="status-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={outfit.isActive}
                      onChange={() => toggleOutfitStatus(outfit.id)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className={`status-text ${outfit.isActive ? 'active' : 'inactive'}`}>
                    {outfit.isActive ? 'Live' : 'Hidden'}
                  </span>
                </div>
                
                <div className="action-buttons">
                  <button 
                    className="btn-action btn-edit"
                    onClick={() => handleEditOutfit(outfit)}
                    title="Edit Outfit & Hotspots"
                  >
                    ✏️
                  </button>
                  <button 
                    className="btn-action btn-delete"
                    onClick={() => handleDeleteOutfit(outfit.id)}
                    title="Delete Outfit"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {outfitsData.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">👘</div>
          <h3>No Outfits Created</h3>
          <p>Create your first interactive outfit experience</p>
          <button className="btn btn-primary" onClick={handleAddOutfit}>
            Create First Outfit
          </button>
        </div>
      )}

      {showOutfitModal && editingOutfit && (
        <OutfitEditor
          outfit={editingOutfit}
          onSave={handleSaveOutfit}
          onCancel={() => {
            setShowOutfitModal(false);
            setEditingOutfit(null);
          }}
        />
      )}
    </div>
  );
};

// Modal Components
interface TrendModalProps {
  trend: TrendSection;
  onSave: (trend: TrendSection) => void;
  onCancel: () => void;
}

const TrendModal: React.FC<TrendModalProps> = ({ trend, onSave, onCancel }) => {
  const [formData, setFormData] = useState<TrendSection>(trend);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content trend-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{trend.id ? 'Edit Trend' : 'Add New Trend'}</h2>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="title">Trend Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g., Y2K Revival"
                required
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe this fashion trend..."
                rows={3}
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="image">Image URL *</label>
              <input
                type="url"
                id="image"
                name="image"
                value={formData.image}
                onChange={handleChange}
                placeholder="https://example.com/trend-image.jpg"
                required
              />
              <small>Recommended size: 800x600px</small>
            </div>

            <div className="form-group full-width">
              <label htmlFor="linkUrl">Link URL *</label>
              <input
                type="text"
                id="linkUrl"
                name="linkUrl"
                value={formData.linkUrl}
                onChange={handleChange}
                placeholder="/shop?trend=y2k"
                required
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {trend.id ? 'Update Trend' : 'Create Trend'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Advanced Shop the Look Editor with Image Upload and Hotspot Placement
interface ShopLookEditorProps {
  look: ShopLookSection;
  onSave: (look: ShopLookSection) => void;
  onCancel: () => void;
}

const ShopLookEditor: React.FC<ShopLookEditorProps> = ({ look, onSave, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<ShopLookSection>(look);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [selectedHotspot, setSelectedHotspot] = useState<number | null>(null);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showCustomTitleInput, setShowCustomTitleInput] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    console.log('🛍️ ShopLookEditor: Look prop changed:', look.id, look.title);
    setFormData(look);
  }, [look]);

  const loadProducts = async () => {
    try {
      const response = await adminApi.getProducts({ limit: 1000 });
      const productsData = Array.isArray(response) ? response : response.products || [];
      setProducts(productsData.filter((product: AdminProduct) => product.inStock));
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!isEditingMode) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newHotspot: Hotspot = {
      id: Date.now(),
      x: Math.round(x),
      y: Math.round(y),
      productId: 0
    };

    setFormData(prev => ({
      ...prev,
      hotspots: [...prev.hotspots, newHotspot]
    }));

    setSelectedHotspot(newHotspot.id);
    setShowProductSelector(true);
  };

  const handleHotspotClick = (hotspotId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedHotspot(hotspotId);
    setShowProductSelector(true);
  };

  const handleProductSelect = (productId: number) => {
    if (productId === -1) {
      // Show custom title input for SOLD OUT option
      setShowCustomTitleInput(true);
      return;
    }
    
    if (selectedHotspot) {
      setFormData(prev => ({
        ...prev,
        hotspots: prev.hotspots.map(h => 
          h.id === selectedHotspot ? { ...h, productId, customTitle: undefined } : h
        )
      }));
    }
    setShowProductSelector(false);
    setSelectedHotspot(null);
  };

  const handleCustomTitleSubmit = () => {
    if (selectedHotspot) {
      setFormData(prev => ({
        ...prev,
        hotspots: prev.hotspots.map(h => 
          h.id === selectedHotspot ? { ...h, productId: -1, customTitle: customTitle || 'SOLD' } : h
        )
      }));
    }
    setShowProductSelector(false);
    setShowCustomTitleInput(false);
    setSelectedHotspot(null);
    setCustomTitle('');
  };

  const handleDeleteHotspot = (hotspotId: number) => {
    setFormData(prev => ({
      ...prev,
      hotspots: prev.hotspots.filter(h => h.id !== hotspotId)
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingImages(true);
    const fileArray = Array.from(files);
    const uploadedUrls: string[] = [];
    
    try {
      // Upload files one by one to avoid issues
      for (const file of fileArray) {
        try {
          const formData = new FormData();
          formData.append('image', await compressImageFile(file));
          
          const response = await fetch(`${API_BASE_URL}/admin/upload-image`, {
            method: 'POST',
            body: formData
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('📸 Upload result imageUrl:', result.imageUrl);
            uploadedUrls.push(result.imageUrl);
          } else {
            console.error('Failed to upload:', file.name);
          }
        } catch (error) {
          console.error('Error uploading:', file.name, error);
        }
      }
      
      // Update state with all uploaded images
      if (uploadedUrls.length > 0) {
        setFormData(prev => {
          const allImages = [...(prev.images || []), ...uploadedUrls];
          return {
            ...prev,
            images: allImages,
            image: allImages[0] || prev.image
          };
        });
      }
    } finally {
      setUploadingImages(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.hotspots.some(h => h.productId === 0)) {
      showToast('Please assign products or SOLD OUT to all hotspots before saving.', 'error');
      return;
    }
    onSave(formData);
  };

  const filteredProducts = products.filter(product =>
    searchTerm === '' || 
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content shoplook-editor" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{look.id ? 'Edit Shop the Look' : 'Create New Look'}</h2>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="modal-form">
          {/* Basic Info */}
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="title">Look Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Vintage Office Look"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status *</label>
              <select
                id="status"
                name="status"
                value={formData.status || 'draft'}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                required
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe this complete look..."
                rows={2}
              />
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="image-upload-section">
            <h3>Outfit Images</h3>
            <div className="upload-options">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={uploadingImages}
                style={{ display: 'none' }}
                id="image-upload"
              />
              <label htmlFor="image-upload" className={`btn btn-secondary ${uploadingImages ? 'disabled' : ''}`}>
                {uploadingImages ? '⏳ Uploading...' : '📁 Upload Images (Multiple)'}
              </label>
              <span style={{ margin: '0 12px', color: '#666' }}>or</span>
              <input
                type="text"
                value={formData.image}
                onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))}
                placeholder="Auto-filled from uploads or enter URL manually"
                style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
            
            {/* Show all uploaded images */}
            {formData.images && formData.images.length > 0 && (
              <div className="uploaded-images-preview">
                <h4>Uploaded Images ({formData.images.length})</h4>
                <div className="images-grid">
                  {formData.images.map((img, index) => (
                    <div key={index} className="image-preview">
                      <img src={proxyBucketUrl(img)} alt={`Slide ${index + 1}`} />
                      <div className="image-controls">
                        <button 
                          type="button" 
                          className="move-image"
                          onClick={() => {
                            if (index > 0) {
                              const newImages = [...formData.images!];
                              [newImages[index], newImages[index - 1]] = [newImages[index - 1], newImages[index]];
                              setFormData(prev => ({ 
                                ...prev, 
                                images: newImages,
                                image: newImages[0]
                              }));
                            }
                          }}
                          disabled={index === 0}
                          title="Move left"
                        >
                          ←
                        </button>
                        <button 
                          type="button" 
                          className="move-image"
                          onClick={() => {
                            if (index < formData.images!.length - 1) {
                              const newImages = [...formData.images!];
                              [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
                              setFormData(prev => ({ 
                                ...prev, 
                                images: newImages,
                                image: newImages[0]
                              }));
                            }
                          }}
                          disabled={index === formData.images!.length - 1}
                          title="Move right"
                        >
                          →
                        </button>
                        <button 
                          type="button" 
                          className="remove-image"
                          onClick={() => {
                            const newImages = formData.images!.filter((_, i) => i !== index);
                            setFormData(prev => ({ 
                              ...prev, 
                              images: newImages,
                              image: newImages[0] || ''
                            }));
                          }}
                        >
                          ×
                        </button>
                      </div>
                      {index === 0 && <span className="main-image-badge">Main</span>}
                      <span className="slide-number">#{index + 1}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Interactive Hotspot Editor */}
          {formData.image && (
            <div className="hotspot-editor">
              <div className="editor-header">
                <h3>Hotspot Editor</h3>
                <div className="editor-controls">
                  <button
                    type="button"
                    className={`btn ${isEditingMode ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setIsEditingMode(!isEditingMode)}
                  >
                    {isEditingMode ? '✅ Done Editing' : '✏️ Add Hotspots'}
                  </button>
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '12px' }}>
                    {isEditingMode ? 'Click on the image to add hotspots' : `${formData.hotspots.length} hotspots created`}
                  </span>
                </div>
              </div>

              <div className="image-editor" style={{ position: 'relative', border: '2px solid #ddd', borderRadius: '8px', overflow: 'hidden', background: '#f8f9fc', maxWidth: '100%' }}>
                <img
                  src={proxyBucketUrl(formData.image)}
                  alt="Outfit"
                  onClick={handleImageClick}
                  style={{
                    width: '100%',
                    maxWidth: '500px',
                    height: 'auto',
                    minHeight: '400px',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    cursor: isEditingMode ? 'crosshair' : 'default',
                    display: 'block'
                  }}
                />

                {/* Render Hotspots */}
                {formData.hotspots.map((hotspot) => {
                  const product = products.find(p => p.id === hotspot.productId);
                  return (
                    <div
                      key={hotspot.id}
                      onClick={(e) => handleHotspotClick(hotspot.id, e)}
                      style={{
                        position: 'absolute',
                        left: `${hotspot.x}%`,
                        top: `${hotspot.y}%`,
                        width: '30px',
                        height: '30px',
                        background: hotspot.productId ? 'rgba(34, 197, 94, 0.9)' : 'rgba(239, 68, 68, 0.9)',
                        border: '3px solid white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: 'white',
                        cursor: 'pointer',
                        transform: 'translate(-50%, -50%)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        animation: 'pulse 2s infinite'
                      }}
                      title={product ? product.name : 'Click to assign product'}
                    >
                      {hotspot.productId ? '✓' : '?'}
                      
                      {/* Delete button for hotspots */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteHotspot(hotspot.id);
                        }}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          width: '16px',
                          height: '16px',
                          background: '#dc2626',
                          border: 'none',
                          borderRadius: '50%',
                          color: 'white',
                          fontSize: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {look.id ? 'Update Look' : 'Create Look'}
            </button>
          </div>
        </form>

        {/* Product Selector Modal */}
        {showProductSelector && (
          <div className="product-selector-overlay">
            <div className="product-selector-modal" onClick={(e) => e.stopPropagation()}>
              <div className="selector-header">
                <h3>Select Product for Hotspot</h3>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    flex: 1, 
                    margin: '0 12px', 
                    padding: '8px 12px', 
                    borderRadius: '6px', 
                    border: '1px solid #ddd' 
                  }}
                />
                <button onClick={() => setShowProductSelector(false)}>×</button>
              </div>
              
              <div className="products-grid" style={{ maxHeight: '400px', overflow: 'auto' }}>
                {/* SOLD OUT Option */}
                <div
                  className="product-option"
                  onClick={() => handleProductSelect(-1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    margin: '8px 0',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: '#fef2f2'
                  }}
                >
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#ef4444',
                    borderRadius: '6px',
                    marginRight: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    SOLD
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#ef4444' }}>SOLD OUT</div>
                    <div style={{ color: '#666', fontSize: '12px' }}>No product available</div>
                  </div>
                </div>

                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="product-option"
                    onClick={() => handleProductSelect(product.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      border: '1px solid #eee',
                      borderRadius: '8px',
                      margin: '8px 0',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <img
                      src={proxyBucketUrl(product.images[0]) || PLACEHOLDER_IMAGE}
                      alt={product.name}
                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px', marginRight: '12px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{product.name}</div>
                      <div style={{ color: '#666', fontSize: '12px' }}>€{product.price}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Custom Title Input Modal */}
        {showCustomTitleInput && (
          <div className="product-selector-overlay">
            <div className="product-selector-modal" onClick={(e) => e.stopPropagation()}>
              <div className="selector-header">
                <h3>Enter Custom Title for SOLD Item</h3>
                <button onClick={() => {
                  setShowCustomTitleInput(false);
                  setShowProductSelector(false);
                  setSelectedHotspot(null);
                  setCustomTitle('');
                }}>×</button>
              </div>
              
              <div style={{ padding: '20px' }}>
                <input
                  type="text"
                  placeholder="Enter item name (e.g., 'Vintage Denim Jacket')"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '6px', 
                    border: '1px solid #ddd',
                    marginBottom: '16px',
                    fontSize: '14px'
                  }}
                />
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => {
                      setShowCustomTitleInput(false);
                      setShowProductSelector(false);
                      setSelectedHotspot(null);
                      setCustomTitle('');
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#f3f4f6',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleCustomTitleSubmit}
                    style={{
                      padding: '8px 16px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Set as SOLD
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Outfit Editor Component for Outfits page
interface OutfitEditorProps {
  outfit: OutfitSection;
  onSave: (outfit: OutfitSection) => void;
  onCancel: () => void;
}

const OutfitEditor: React.FC<OutfitEditorProps> = ({ outfit, onSave, onCancel }) => {
  const { showToast } = useToast();
  const [formOutfitData, setFormOutfitData] = useState<OutfitSection>(outfit);
  const [productsForOutfits, setProductsForOutfits] = useState<AdminProduct[]>([]);
  const [isOutfitEditingMode, setIsOutfitEditingMode] = useState(false);
  const [selectedOutfitHotspot, setSelectedOutfitHotspot] = useState<number | null>(null);
  const [showOutfitProductSelector, setShowOutfitProductSelector] = useState(false);
  const [showCustomTitleInput, setShowCustomTitleInput] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [outfitSearchTerm, setOutfitSearchTerm] = useState('');
  const [uploadingOutfitImage, setUploadingOutfitImage] = useState(false);

  useEffect(() => {
    loadProductsForOutfits();
  }, []);

  useEffect(() => {
    console.log('👕 OutfitEditor: Outfit prop changed:', outfit.id, outfit.title);
    setFormOutfitData(outfit);
  }, [outfit]);

  const loadProductsForOutfits = async () => {
    try {
      const response = await adminApi.getProducts({ limit: 1000 });
      const productsData = Array.isArray(response) ? response : response.products || [];
      setProductsForOutfits(productsData.filter((product: AdminProduct) => product.inStock));
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const handleOutfitImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!isOutfitEditingMode) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newOutfitHotspot: OutfitHotspot = {
      id: Date.now(),
      x: Math.round(x),
      y: Math.round(y),
      productId: 0
    };

    setFormOutfitData(prev => ({
      ...prev,
      hotspots: [...prev.hotspots, newOutfitHotspot]
    }));

    setSelectedOutfitHotspot(newOutfitHotspot.id);
    setShowOutfitProductSelector(true);
  };

  const handleOutfitHotspotClick = (hotspotId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOutfitHotspot(hotspotId);
    setShowOutfitProductSelector(true);
  };

  const handleOutfitProductSelect = (productId: number) => {
    if (productId === -1) {
      // Show custom title input for SOLD OUT option
      setShowCustomTitleInput(true);
      return;
    }
    
    if (selectedOutfitHotspot) {
      setFormOutfitData(prev => ({
        ...prev,
        hotspots: prev.hotspots.map(h => 
          h.id === selectedOutfitHotspot ? { ...h, productId, customTitle: undefined } : h
        )
      }));
    }
    setShowOutfitProductSelector(false);
    setSelectedOutfitHotspot(null);
  };

  const handleCustomTitleSubmit = () => {
    if (selectedOutfitHotspot) {
      setFormOutfitData(prev => ({
        ...prev,
        hotspots: prev.hotspots.map(h => 
          h.id === selectedOutfitHotspot ? { ...h, productId: -1, customTitle: customTitle || 'SOLD' } : h
        )
      }));
    }
    setShowOutfitProductSelector(false);
    setShowCustomTitleInput(false);
    setSelectedOutfitHotspot(null);
    setCustomTitle('');
  };

  const handleDeleteOutfitHotspot = (hotspotId: number) => {
    setFormOutfitData(prev => ({
      ...prev,
      hotspots: prev.hotspots.filter(h => h.id !== hotspotId)
    }));
  };

  const handleOutfitImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Clear input immediately to prevent ERR_UPLOAD_FILE_CHANGED
      e.target.value = '';
      
      setUploadingOutfitImage(true);
      try {
        const formData = new FormData();
        formData.append('image', await compressImageFile(file));
        
        console.log(`🔄 Uploading outfit image: ${file.name}...`);
        
        const response = await fetch(`${API_BASE_URL}/admin/upload-image`, {
          method: 'POST',
          body: formData
        });
        
        console.log(`📦 Outfit upload response:`, response.status, response.statusText);
        
        if (response.ok) {
          const result = await response.json();
          console.log(`✅ Outfit upload success:`, result);
          setFormOutfitData(prev => ({ ...prev, image: result.imageUrl }));
        } else {
          const errorText = await response.text();
          console.error('❌ Failed to upload outfit image:', response.status, errorText);
          showToast(`Failed to upload image: ${response.status} - ${errorText}`, 'error');
        }
      } catch (error) {
        console.error('💥 Network error uploading outfit image:', error);
        showToast(`Network error uploading image: ${error}`, 'error');
      } finally {
        setUploadingOutfitImage(false);
      }
    }
  };

  const handleOutfitSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formOutfitData.hotspots.some(h => h.productId === 0)) {
      showToast('Please assign products or SOLD OUT to all hotspots before saving.', 'error');
      return;
    }
    onSave(formOutfitData);
  };

  const filteredOutfitProducts = productsForOutfits.filter(product =>
    outfitSearchTerm === '' || 
    product.name.toLowerCase().includes(outfitSearchTerm.toLowerCase())
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content shoplook-editor" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{outfit.id ? 'Edit Outfit' : 'Create New Outfit'}</h2>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleOutfitSubmit} className="modal-form">
          {/* Basic Info */}
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="outfit-title">Outfit Title *</label>
              <input
                type="text"
                id="outfit-title"
                name="title"
                value={formOutfitData.title}
                onChange={(e) => setFormOutfitData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Parisian Chic"
                required
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="outfit-description">Description</label>
              <textarea
                id="outfit-description"
                name="description"
                value={formOutfitData.description}
                onChange={(e) => setFormOutfitData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe this outfit style..."
                rows={2}
              />
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="image-upload-section">
            <h3>Outfit Image</h3>
            <div className="upload-options">
              <input
                type="file"
                accept="image/*"
                onChange={handleOutfitImageUpload}
                disabled={uploadingOutfitImage}
                style={{ display: 'none' }}
                id="outfit-image-upload"
              />
              <label htmlFor="outfit-image-upload" className={`btn btn-secondary ${uploadingOutfitImage ? 'disabled' : ''}`}>
                {uploadingOutfitImage ? '⏳ Uploading...' : '📁 Upload Image'}
              </label>
              <span style={{ margin: '0 12px', color: '#666' }}>or</span>
              <input
                type="url"
                value={formOutfitData.image}
                onChange={(e) => setFormOutfitData(prev => ({ ...prev, image: e.target.value }))}
                placeholder="Enter image URL"
                style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd' }}
              />
            </div>
          </div>

          {/* Interactive Hotspot Editor - Use same dimensions as Outfits page (600px height) */}
          {formOutfitData.image && (
            <div className="hotspot-editor">
              <div className="editor-header">
                <h3>Hotspot Editor</h3>
                <div className="editor-controls">
                  <button
                    type="button"
                    className={`btn ${isOutfitEditingMode ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setIsOutfitEditingMode(!isOutfitEditingMode)}
                  >
                    {isOutfitEditingMode ? '✅ Done Editing' : '✏️ Add Hotspots'}
                  </button>
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '12px' }}>
                    {isOutfitEditingMode ? 'Click on the image to add hotspots' : `${formOutfitData.hotspots.length} hotspots created`}
                  </span>
                </div>
              </div>

              <div className="image-editor" style={{ position: 'relative', border: '2px solid #ddd', borderRadius: '8px', overflow: 'hidden', background: '#f8f9fc', maxWidth: '100%' }}>
                <img
                  src={proxyBucketUrl(formOutfitData.image)}
                  alt="Outfit"
                  onClick={handleOutfitImageClick}
                  style={{
                    width: '100%',
                    maxWidth: '500px',
                    height: 'auto',
                    minHeight: '400px',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    cursor: isOutfitEditingMode ? 'crosshair' : 'default',
                    display: 'block'
                  }}
                />

                {/* Render Hotspots */}
                {formOutfitData.hotspots.map((hotspot) => {
                  const product = productsForOutfits.find(p => p.id === hotspot.productId);
                  return (
                    <div
                      key={hotspot.id}
                      onClick={(e) => handleOutfitHotspotClick(hotspot.id, e)}
                      style={{
                        position: 'absolute',
                        left: `${hotspot.x}%`,
                        top: `${hotspot.y}%`,
                        width: '30px',
                        height: '30px',
                        background: hotspot.productId ? 'rgba(34, 197, 94, 0.9)' : 'rgba(239, 68, 68, 0.9)',
                        border: '3px solid white',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: 'white',
                        cursor: 'pointer',
                        transform: 'translate(-50%, -50%)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        animation: 'pulse 2s infinite'
                      }}
                      title={product ? product.name : 'Click to assign product'}
                    >
                      {hotspot.productId ? '✓' : '?'}
                      
                      {/* Delete button for hotspots */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOutfitHotspot(hotspot.id);
                        }}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          width: '16px',
                          height: '16px',
                          background: '#dc2626',
                          border: 'none',
                          borderRadius: '50%',
                          color: 'white',
                          fontSize: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {outfit.id ? 'Update Outfit' : 'Create Outfit'}
            </button>
          </div>
        </form>

        {/* Product Selector Modal */}
        {showOutfitProductSelector && (
          <div className="product-selector-overlay">
            <div className="product-selector-modal" onClick={(e) => e.stopPropagation()}>
              <div className="selector-header">
                <h3>Select Product for Hotspot</h3>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={outfitSearchTerm}
                  onChange={(e) => setOutfitSearchTerm(e.target.value)}
                  style={{ 
                    flex: 1, 
                    margin: '0 12px', 
                    padding: '8px 12px', 
                    borderRadius: '6px', 
                    border: '1px solid #ddd' 
                  }}
                />
                <button onClick={() => setShowOutfitProductSelector(false)}>×</button>
              </div>
              
              <div className="products-grid" style={{ maxHeight: '400px', overflow: 'auto' }}>
                {/* SOLD OUT Option */}
                <div
                  className="product-option"
                  onClick={() => handleOutfitProductSelect(-1)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    border: '2px solid #ef4444',
                    borderRadius: '8px',
                    margin: '8px 0',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor: '#fef2f2'
                  }}
                >
                  <div style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#ef4444',
                    borderRadius: '6px',
                    marginRight: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold'
                  }}>
                    SOLD
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#ef4444' }}>SOLD OUT</div>
                    <div style={{ color: '#666', fontSize: '12px' }}>No product available</div>
                  </div>
                </div>

                {filteredOutfitProducts.map((product) => (
                  <div
                    key={product.id}
                    className="product-option"
                    onClick={() => handleOutfitProductSelect(product.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      border: '1px solid #eee',
                      borderRadius: '8px',
                      margin: '8px 0',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <img
                      src={proxyBucketUrl(product.images[0]) || PLACEHOLDER_IMAGE}
                      alt={product.name}
                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px', marginRight: '12px' }}
                    />
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px' }}>{product.name}</div>
                      <div style={{ color: '#666', fontSize: '12px' }}>€{product.price}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Custom Title Input Modal */}
        {showCustomTitleInput && (
          <div className="product-selector-overlay">
            <div className="product-selector-modal" onClick={(e) => e.stopPropagation()}>
              <div className="selector-header">
                <h3>Enter Custom Title for SOLD Item</h3>
                <button onClick={() => {
                  setShowCustomTitleInput(false);
                  setShowOutfitProductSelector(false);
                  setSelectedOutfitHotspot(null);
                  setCustomTitle('');
                }}>×</button>
              </div>
              
              <div style={{ padding: '20px' }}>
                <input
                  type="text"
                  placeholder="Enter item name (e.g., 'Vintage Denim Jacket')"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '12px', 
                    borderRadius: '6px', 
                    border: '1px solid #ddd',
                    marginBottom: '16px',
                    fontSize: '14px'
                  }}
                />
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => {
                      setShowCustomTitleInput(false);
                      setShowOutfitProductSelector(false);
                      setSelectedOutfitHotspot(null);
                      setCustomTitle('');
                    }}
                    style={{
                      padding: '8px 16px',
                      background: '#f3f4f6',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleCustomTitleSubmit}
                    style={{
                      padding: '8px 16px',
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Set as SOLD
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContent;